import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useSiteContent } from '../utils/useSiteContent';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Sparkles, Heart, MessageCircle, Users, Check, ArrowRight, Shield } from 'lucide-react';
import { toast } from 'sonner';

export default function Sanctuary() {
  const [tier, setTier] = useState(null);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const t = useSiteContent('sanctuary.');

  useEffect(() => {
    api.get('/membership/tier').then(({ data }) => setTier(data)).catch(() => {});
    if (user) {
      api.get('/membership/me').then(({ data }) => setMembership(data)).catch(() => {});
    }
  }, [user]);

  const startCheckout = async () => {
    if (!user) {
      navigate('/login', { state: { from: '/sanctuary' } });
      return;
    }
    if (membership?.is_member) {
      navigate('/sanctuary/dashboard');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/membership/checkout', { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not start checkout';
      toast.error(msg);
      setLoading(false);
    }
  };

  const price = tier?.price ?? 6.99;

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1441974231531-c6227db76b6e?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85)', backgroundSize: 'cover', backgroundPosition: 'center', filter: 'saturate(0.95) brightness(1.05)' }} aria-hidden />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(253, 251, 247, 0.55) 0%, rgba(253, 251, 247, 0.75) 55%, rgba(253, 251, 247, 0.95) 100%)' }} aria-hidden />
        <div className="max-w-5xl mx-auto px-6 pt-20 pb-16 relative text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ backgroundColor: '#E8C26525', border: '1px solid #E8C26580' }}>
            <Sparkles className="w-4 h-4" style={{ color: '#B38A2E' }} />
            <span className="text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#4A3D1E', fontWeight: 600 }}>{t('hero.pill', 'Introducing a quieter space')}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-5" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29', lineHeight: 1.15 }} data-testid="sanctuary-hero-title">
            {t('hero.title', 'The Sanctuary')}
          </h1>
          <p className="text-lg sm:text-xl max-w-2xl mx-auto mb-8" style={{ fontFamily: 'Lora, serif', color: '#4A423B', lineHeight: 1.7, fontStyle: 'italic' }}>
            {tier?.tagline || t('hero.tagline', 'A quieter, more intimate space for believers walking the same road.')}
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <button
              onClick={startCheckout}
              disabled={loading}
              className="px-8 py-4 rounded-full flex items-center gap-2 transition-all duration-300 hover:-translate-y-1 disabled:opacity-50"
              style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 700, fontSize: '16px', boxShadow: '0 12px 30px rgba(229, 122, 68, 0.35)' }}
              data-testid="sanctuary-cta-primary"
            >
              <Sparkles className="w-5 h-5" />
              <span>{loading ? 'Opening checkout…' : membership?.is_member ? 'Enter The Sanctuary' : `Join for $${price.toFixed(2)}/mo`}</span>
              {!loading && <ArrowRight className="w-5 h-5" />}
            </button>
            <a href="#what-you-get" className="px-6 py-3 rounded-full text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724', border: '1.5px solid #2C272430', fontWeight: 600 }}>
              What's inside
            </a>
          </div>
          <p className="text-xs mt-5" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
            {t('hero.assurance', 'All existing articles, the feelings guide, and the public prayer wall remain 100% free. The Sanctuary is a deeper, optional layer.')}
          </p>
        </div>
      </section>

      {/* What you get */}
      <section id="what-you-get" className="max-w-5xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{t('benefits.title', "What's inside the Sanctuary")}</h2>
          <p className="text-base max-w-xl mx-auto" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>{t('benefits.subtitle', 'A small, intentional space — not more content, but more connection.')}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <BenefitCard
            icon={<Heart className="w-6 h-6" />}
            title={t('benefits.card1.title', 'Private prayer wall')}
            body={t('benefits.card1.body', 'Post intentions only members can see. Others pray over you — you pray over them. Held quietly, prayerfully.')}
            tint="#E57A44"
            testid="benefit-prayer"
          />
          <BenefitCard
            icon={<MessageCircle className="w-6 h-6" />}
            title={t('benefits.card2.title', 'Shape what gets written')}
            body={t('benefits.card2.body', "Share the topics closest to your heart. Your voice guides what I write next — and you may find you're not alone in what you're carrying.")}
            tint="#78A18E"
            testid="benefit-requests"
          />
          <BenefitCard
            icon={<Users className="w-6 h-6" />}
            title={t('benefits.card3.title', 'A closer circle')}
            body={t('benefits.card3.body', 'Fewer voices, more depth. A small community of believers walking the same road, encouraging each other with scripture.')}
            tint="#5B7C99"
            testid="benefit-community"
          />
        </div>
      </section>

      {/* Pricing card */}
      <section className="max-w-3xl mx-auto px-6 py-12">
        <div className="p-10 rounded-3xl text-center relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF5E5 100%)', boxShadow: '0 20px 60px rgba(229, 122, 68, 0.15)', border: '1px solid #E8C26540' }}>
          <div className="inline-block px-4 py-1 rounded-full mb-4" style={{ backgroundColor: '#E8C265', color: '#2C2A29', fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
            {t('pricing.pill', 'Members Only')}
          </div>
          <h3 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{t('pricing.title', 'The Sanctuary')}</h3>
          <div className="mb-6">
            <span className="text-6xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#E57A44' }}>${price.toFixed(2)}</span>
            <span className="text-lg ml-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>/month</span>
          </div>
          <div className="text-left max-w-md mx-auto space-y-3 mb-8">
            {(tier?.benefits || []).map((b, i) => (
              <div key={i} className="flex items-start gap-3">
                <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#78A18E', strokeWidth: 2.5 }} />
                <span style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29', fontSize: '14px' }}>{b}</span>
              </div>
            ))}
          </div>
          <button
            onClick={startCheckout}
            disabled={loading}
            className="px-8 py-4 rounded-full inline-flex items-center gap-2 transition-all duration-300 hover:-translate-y-1 disabled:opacity-50"
            style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 700, fontSize: '16px' }}
            data-testid="sanctuary-cta-pricing"
          >
            <Sparkles className="w-5 h-5" />
            <span>{loading ? 'Opening checkout…' : membership?.is_member ? 'Enter The Sanctuary' : 'Join The Sanctuary'}</span>
          </button>
          <div className="mt-5 flex items-center justify-center gap-2 text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
            <Shield className="w-4 h-4" style={{ color: '#78A18E' }} />
            <span>{t('pricing.assurance', 'Cancel anytime · Secure Stripe checkout · No commitments')}</span>
          </div>
        </div>
      </section>

      {/* Reassurance */}
      <section className="max-w-3xl mx-auto px-6 pb-24">
        <div className="p-6 rounded-2xl text-center" style={{ backgroundColor: '#F3EFE7', border: '1px solid #E6E2D8' }}>
          <p className="text-sm" style={{ fontFamily: 'Lora, serif', color: '#595553', lineHeight: 1.8, fontStyle: 'italic' }}>
            {t('scripture', '"As iron sharpens iron, so one person sharpens another." — Proverbs 27:17')}
          </p>
        </div>
        {membership?.is_member && (
          <div className="mt-6 text-center">
            <Link to="/sanctuary/dashboard" className="text-sm underline" style={{ fontFamily: 'Nunito, sans-serif', color: '#E57A44', fontWeight: 600 }} data-testid="sanctuary-goto-dashboard">
              Go to your Sanctuary dashboard →
            </Link>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}

function BenefitCard({ icon, title, body, tint, testid }) {
  return (
    <div className="p-7 rounded-3xl transition-all duration-300 hover:-translate-y-2" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)', border: '1px solid #F0EBE0' }} data-testid={testid}>
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${tint}20`, color: tint }}>
        {icon}
      </div>
      <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{title}</h3>
      <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.7, fontSize: '15px' }}>{body}</p>
    </div>
  );
}
